import { useState, useEffect } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { Box, Text } from '@chakra-ui/react';
import Navbar from './Navbar.jsx';
import Card from './Card.jsx';
import AccessDenied from './auth/AccessDenied.jsx';
import { useApplications } from '../hooks/useApplications.js';
import { isForbiddenError } from '../utils/httpErrors.js';

const SELECTED_PROJECT_STORAGE_KEY = 'selectedProject';

const readSelectedProjectFromStorage = () => {
  return localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY) || null;
};

export default function MainLayout() {
  const location = useLocation();
  const isApplicationIndependentRoute =
    location.pathname.startsWith('/uao/') || location.pathname.startsWith('/campaigns');
  const {
    applications,
    loading: applicationsLoading,
    error: applicationsError,
    accessDenied: applicationsAccessDenied,
    reload: refreshApplications,
  } = useApplications({ autoLoad: !isApplicationIndependentRoute });

  const [selectedProject, setSelectedProject] = useState(() => {
    return readSelectedProjectFromStorage();
  });

  useEffect(() => {
    if (selectedProject) {
      localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, selectedProject);
    } else {
      localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY);
    }
  }, [selectedProject]);

  useEffect(() => {
    const handleSelectedProjectChange = (event) => {
      if (event.key !== SELECTED_PROJECT_STORAGE_KEY) {
        return;
      }

      setSelectedProject(event.newValue || null);
    };

    window.addEventListener('storage', handleSelectedProjectChange);

    return () => {
      window.removeEventListener('storage', handleSelectedProjectChange);
    };
  }, []);

  useEffect(() => {
    if (isApplicationIndependentRoute || applicationsLoading || applicationsAccessDenied) {
      return;
    }

    if (applications.length === 0) {
      if (selectedProject) {
        setSelectedProject(null);
      }
      return;
    }

    const hasSelectedProject = applications.some((app) => app.uuid === selectedProject);

    if (hasSelectedProject) {
      return;
    }

    const savedProject = readSelectedProjectFromStorage();

    if (savedProject && applications.some((app) => app.uuid === savedProject)) {
      setSelectedProject(savedProject);
      return;
    }

    setSelectedProject(applications[0].uuid);
  }, [
    applications,
    applicationsAccessDenied,
    applicationsLoading,
    isApplicationIndependentRoute,
    selectedProject,
  ]);

  const selectedApplication = applications.find((app) => app.uuid === selectedProject);
  const selectedAppName = selectedApplication?.name || null;
  const selectedAppShortName = selectedApplication?.short_name || null;
  const selectedAppUuid = selectedApplication?.uuid || null;

  if (applicationsLoading) {
    return <Box className="app-container" bg="white" minH="100vh" />;
  }

  if (applicationsError && !isApplicationIndependentRoute) {
    return (
      <div className="app-container">
        <Navbar
          applications={applications}
          hideApplicationSelector={applicationsAccessDenied || isApplicationIndependentRoute}
          selectedProject={selectedProject}
          onChangeProject={setSelectedProject}
        />
        <main className="content">
          {isForbiddenError(applicationsError) ? (
            <AccessDenied message="You do not have permission to view applications." />
          ) : (
            <Card title="Error" subtitle="Failed to load applications">
              <Box h="2" />
              <Text color="red.500">Error: {applicationsError}</Text>
            </Card>
          )}
        </main>
      </div>
    );
  }

  const outletContext = {
    applications,
    selectedProject,
    setSelectedProject,
    selectedApplication,
    selectedAppName,
    selectedAppShortName,
    selectedAppUuid,
    refreshApplications,
  };

  return (
    <div
      className={`app-container${location.pathname.replace(/\/$/, '') === '/creative-manager' ? ' cm-shell' : ''}`}
    >
      <Navbar
        applications={applications}
        hideApplicationSelector={applicationsAccessDenied || isApplicationIndependentRoute}
        selectedProject={selectedProject}
        onChangeProject={setSelectedProject}
      />
      <main
        className={`content${location.pathname.replace(/\/$/, '') === '/creative-manager' ? ' creative-manager-content' : ''}`}
      >
        <Outlet context={outletContext} />
      </main>
    </div>
  );
}

export function useLayoutContext() {
  return useOutletContext();
}
